<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: BlogPost                                              |
| Section: views/blog                                              |
| Purpose: Blog post route rendering a single article entry.       |
+------------------------------------------------------------------+
-->
<template>
    <!-- Dynamic anchor links based on h2 headings -->
    <div class="anchor-links" v-if="blogSections.length && post" ref="anchorLinks">
        <h1>{{ post.title }}</h1>
        <ul>
            <li v-for="section in blogSections" :key="section.id">
                <a href="#" @click.prevent="scrollToSection(section.id)">{{ section.label }}</a>
            </li>
        </ul>
    </div>

    <LoadingSpinner v-if="loading" />

    <div v-if="!loading">
        <div class="post_wrapper wrapper" v-if="post" data-prerender-kind="story"
            :style="{ marginTop: '-' + anchorHeight + 'px' }">
            <div class="post_header">
                <BtnSecondary class="BtnSecondary" :label="$t('blogPost.back')" :url="localePath('/verhalen')" />
                <h1>{{ post.title }}</h1>
                <p>{{ $t('blogPost.publishedBy', { author: post.author, date: formatDate(post.published) }) }}</p>
            </div>

            <div v-if="blogSections.length" class="table-of-contents">
                <h2>{{ $t('blogPost.tableOfContents') }}</h2>
                <ul>
                    <li v-for="(section, index) in blogSections" :key="section.id">
                        <a href="#" @click.prevent="scrollToSection(section.id)">
                            {{ index + 1 }}. {{ section.label }}
                        </a>
                    </li>
                </ul>
            </div>

            <div v-if="storyAudioEnabled && storyAudio && currentAudioSrc" class="story-audio">
                <p class="story-audio__label">Luister naar dit verhaal</p>
                <audio ref="storyAudioPlayer" :src="currentAudioSrc" @ended="playNextAudioChunk"
                    @loadedmetadata="updateAudioProgress" @pause="handleAudioPause" @play="isStoryAudioPlaying = true"
                    @timeupdate="handleAudioTimeUpdate"></audio>
                <div class="story-audio__main">
                    <div class="story-audio__controls">
                        <button type="button" class="story-audio__play"
                            :aria-label="isStoryAudioPlaying ? 'Pauze' : 'Afspelen'" @click="toggleStoryAudio">
                            <span class="story-audio__play-icon"
                                :class="{ 'story-audio__play-icon--pause': isStoryAudioPlaying }"></span>
                        </button>
                    </div>
                    <div class="story-audio__timeline">
                        <div class="story-audio__progress" role="slider" tabindex="0" aria-label="Voortgang verhaal"
                            :aria-valuenow="Math.round(storyAudioProgress)" aria-valuemin="0" aria-valuemax="100"
                            @click="seekStoryAudio" @keydown.left.prevent="seekStoryAudioBy(-10)"
                            @keydown.right.prevent="seekStoryAudioBy(10)">
                            <span :style="{ width: `${storyAudioProgress}%` }"></span>
                        </div>
                        <div class="story-audio__time">
                            <span>{{ formattedStoryAudioElapsed }}</span>
                            <span>{{ formattedStoryAudioTotal }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Main image check -->
            <div v-if="post.image && (!post.images || !post.images.length) && $route.params.slug !== 'in-de-schaduw-van-4-mei'"
                class="post_image">
                <img :src="post.image" :alt="post.title" />
            </div>

            <!-- Special YouTube embed for "in-de-schaduw-van-4-mei" -->
            <div v-if="$route.params.slug === 'in-de-schaduw-van-4-mei'" class="post_video">
                <iframe src="https://www.youtube.com/embed/mYiBJr_LE6M?si=UwE6LuGnionARvY-" title="YouTube video player"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
            </div>

            <div v-if="post.images && post.images.length" class="post_images">
                <div v-for="(image, index) in post.images" :key="index" class="post_image">
                    <img :src="image" :alt="`Image ${index + 1} of ${post.title}`" />
                </div>
            </div>

            <!-- If PDF exists, render it -->
            <div v-if="post.pdfFile" class="post_pdf">
                <iframe :src="post.pdfFile" width="100%" height="600px" style="border: none;"></iframe>
            </div>

            <!-- Otherwise, render the content -->
            <div v-else class="post_content" v-html="renderContent(post.content)"></div>

            <div class="quote_wrapper">
                <hr>
            </div>

            <div class="post_suggestions_container">
                <TitleGroup :label="$t('blogPost.storiesLabel')" :title="$t('blogPost.moreStories')" />
                <div class="post_suggestions_wrapper">
                    <SuggestedBlogPost v-for="suggestedPost in randomPosts" :key="suggestedPost.id"
                        :label="suggestedPost.title" :thumbnail="suggestedPost.image || 'default_image.jpg'"
                        :url="localePath(`/verhalen/${suggestedPost.slug}`)"
                        @click="forceReload(localePath(`/verhalen/${suggestedPost.slug}`))" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
// import { getPostBySlug, getRandomPosts } from '@/api/blogService';
import { useLocale } from '@/composables/useLocale';
import TitleGroup from '@/atoms/TitleGroup.vue';
import SuggestedBlogPost from '@/components/SuggestedBlogPost.vue';
import BtnSecondary from '@/atoms/BtnSecondary.vue';
import LoadingSpinner from '@/atoms/LoadingSpinner.vue';
import { applyBlogPostSeo, applyBreadcrumbSeo } from '@/utils/seoUtils';
import { markMachineTranslationDetected } from '@/utils/machineTranslationNotice';

const sentencePattern = /[^.!?。！？]+[.!?。！？]+["'”’)]*|[^.!?。！？]+$/g;
const SECTION_PAUSE_MS = 1200;
const STORY_AUDIO_ENABLED = false;

function normalizeAudioWhitespace(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
}

function slugifyAudioSection(value, fallback = 'section') {
    const slug = String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60);

    return slug || fallback;
}

function escapeHtml(value) {
    return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

function formatAudioTime(seconds) {
    if (!Number.isFinite(seconds) || seconds <= 0) {
        return '0:00';
    }

    const roundedSeconds = Math.floor(seconds);
    const minutes = Math.floor(roundedSeconds / 60);
    const remainingSeconds = String(roundedSeconds % 60).padStart(2, '0');
    return `${minutes}:${remainingSeconds}`;
}

export default {
    components: {
        TitleGroup,
        SuggestedBlogPost,
        BtnSecondary,
        LoadingSpinner,
    },
    setup() {
        const { localePath, locale } = useLocale();
        return { localePath, locale };
    },
    data() {
        return {
            post: null,
            localizedStoryLocale: null,
            localizedStorySlug: null,
            randomPosts: [],
            blogSections: [], // Populated dynamically with h2 headings
            storyAudioEnabled: STORY_AUDIO_ENABLED,
            storyAudio: null,
            audioCurrentIndex: 0,
            currentAudioTime: 0,
            currentAudioDuration: 0,
            storyAudioDurations: [],
            pendingAudioSeekTime: null,
            storyAudioSectionPauseTimer: null,
            isStoryAudioPlaying: false,
            activeAudioSentenceId: null,
            anchorHeight: 0,
            loading: true,
        };
    },
    async created() {
        await this.fetchPostData();
    },
    mounted() {
        // Update the anchor-links height on mount and on window resize.
        this.updateAnchorHeight();
        window.addEventListener('resize', this.updateAnchorHeight);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.updateAnchorHeight);
        this.clearStoryAudioSectionPause();
    },
    watch: {
        '$route.params.slug': {
            immediate: true,
            handler() {
                this.fetchPostData();
            }
        },
        '$route.fullPath'() {
            this.markLocalizedStoryDetected();
        },
        '$i18n.locale'() {
            this.fetchPostData();
        },
    },
    methods: {
        async fetchPostData() {
            this.loading = true;
            this.localizedStoryLocale = null;
            this.localizedStorySlug = null;
            this.storyAudio = null;
            this.audioCurrentIndex = 0;
            this.currentAudioTime = 0;
            this.currentAudioDuration = 0;
            this.storyAudioDurations = [];
            this.pendingAudioSeekTime = null;
            this.clearStoryAudioSectionPause();
            this.isStoryAudioPlaying = false;
            this.clearActiveAudioSentence();

            try {
                const slug = this.$route.params.slug;
                const locale = this.$i18n.locale;
                try {
                    try {
                        this.post = require(`@/data/getBlogPost/${slug}_${locale}.json`);
                        if (locale !== 'nl') {
                            this.localizedStoryLocale = locale;
                            this.localizedStorySlug = slug;
                            this.markLocalizedStoryDetected();
                        }
                    } catch {
                        this.post = require(`@/data/getBlogPost/${slug}.json`);
                    }
                } catch (err) {
                    console.error('Could not load local blog post JSON:', err);
                    this.post = null;
                }

                if (this.post) {
                    // Stel de SEO-meta in voor deze blogpost
                    document.documentElement.lang = this.locale;
                    applyBlogPostSeo(this.post, {
                        currentLocale: this.locale,
                        includeTwitter: true,
                        includeJsonLd: true,
                        includeHreflangs: true,
                    });
                    const postTitle = typeof this.post.title === 'string' && this.post.title.trim()
                        ? this.post.title.trim()
                        : this.$t('blogPost.breadcrumbFallbackTitle');

                    // Add breadcrumb schema for blog post
                    const localePrefix = this.locale === 'nl' ? '' : `/${this.locale}`;
                    applyBreadcrumbSeo([
                        { name: this.$t('nav.home'), url: `https://sejarah-thumann.online${localePrefix || '/'}` },
                        { name: this.$t('nav.stories'), url: `https://sejarah-thumann.online${localePrefix}/verhalen` },
                        { name: postTitle, url: `https://sejarah-thumann.online${localePrefix}/verhalen/${this.$route.params.slug}` }
                    ]);

                    // Stel de document title in (optioneel, want applyBlogPostSeo doet dit al)
                    this.setDocumentTitle();

                    // Genereer dynamische blogsecties op basis van h2-koppen
                    this.generateBlogSections();

                    // Na het renderen van de post update je de anchor-height
                    this.$nextTick(() => {
                        this.updateAnchorHeight();
                    });
                }

                if (this.storyAudioEnabled) {
                    await this.loadStoryAudio(slug, locale);
                }

                // Sorteer afbeeldingen indien nodig
                if (this.post && this.post.images && this.post.images.length) {
                    this.post.images.sort((a, b) => {
                        const nameA = a.split('/').pop().toLowerCase();
                        const nameB = b.split('/').pop().toLowerCase();
                        return nameA.localeCompare(nameB);
                    });
                }

                // Haal random posts op (exclusief de huidige)
                try {
                    const locale = this.$i18n.locale;
                    let allPosts;
                    try {
                        allPosts = require(`@/data/getBlogPost/random_${locale}.json`);
                    } catch {
                        allPosts = require('@/data/getBlogPost/random.json');
                    }
                    this.randomPosts = allPosts
                        .filter(post => post.slug !== slug)
                        .sort(() => 0.5 - Math.random())
                        .slice(0, 3);
                } catch (err) {
                    console.error('Failed to load random posts:', err);
                    this.randomPosts = [];
                }

            } catch (err) {
                console.error('Failed to fetch post:', err);
            } finally {
                this.loading = false;
                if (this.$route.query.prerender === '1') {
                    this.$nextTick(() => {
                        document.dispatchEvent(new Event('sejarah-prerender-ready'));
                    });
                }
            }
        },
        markLocalizedStoryDetected() {
            if (
                this.localizedStoryLocale === this.$i18n.locale
                && this.localizedStorySlug === this.$route.params.slug
            ) {
                markMachineTranslationDetected();
            }
        },
        async loadStoryAudio(slug, locale) {
            try {
                const response = await fetch('/audio/stories/manifest.json', { cache: 'no-store' });
                if (!response.ok) {
                    return;
                }

                const manifest = await response.json();
                const entries = Array.isArray(manifest.entries) ? manifest.entries : [];
                this.storyAudio = entries.find(entry => entry.slug === slug && entry.locale === locale) || null;
                if (this.storyAudio) {
                    this.preloadStoryAudioDurations();
                }
            } catch (err) {
                console.error('Failed to load story audio manifest:', err);
                this.storyAudio = null;
            }
        },
        preloadStoryAudioDurations() {
            this.storyAudioDurations = this.storyAudioChunks.map(() => 0);
            this.storyAudioChunks.forEach((chunk, index) => {
                const audio = new Audio(chunk.file);
                audio.preload = 'metadata';
                audio.addEventListener('loadedmetadata', () => {
                    this.storyAudioDurations.splice(index, 1, Number.isFinite(audio.duration) ? audio.duration : 0);
                }, { once: true });
                audio.load();
            });
        },
        generateBlogSections() {
            this.blogSections = [];
            if (this.post && this.post.content && Array.isArray(this.post.content)) {
                this.post.content.forEach((block) => {
                    // Check for h2 headings (default level 2 if not provided)
                    if (block.type === 'heading' && (block.level || 2) === 2) {
                        const headingText = block.children
                            ? block.children.map(child => child.text).join('')
                            : '';
                        if (headingText) {
                            // Create a slug-friendly id
                            const headingId = headingText
                                .toLowerCase()
                                .trim()
                                .replace(/\s+/g, '-')
                                .replace(/[^\w-]+/g, '');
                            // Attach the id to the block so it renders with the heading tag
                            block.id = headingId;
                            // Add this heading to the blogSections array
                            this.blogSections.push({
                                id: headingId,
                                label: headingText
                            });
                        }
                    }
                });
            }
        },
        renderContent(content) {
            if (!content || !Array.isArray(content)) return '';
            const audioRenderState = {
                sectionIndex: 1,
                currentSectionId: '01-intro',
                sectionOffsets: { '01-intro': this.post?.title ? normalizeAudioWhitespace(this.post.title).length + 2 : 0 },
                sentenceIndexes: {},
            };

            return content.map(block => {
                switch (block.type) {
                    case 'paragraph':
                        return `<p>${this.renderAudioChildren(block.children, audioRenderState)}</p>`;
                    case 'heading': {
                        const level = block.level || 2;
                        const headingText = normalizeAudioWhitespace(this.getPlainText(block.children));
                        if (level === 2) {
                            audioRenderState.sectionIndex += 1;
                            audioRenderState.currentSectionId = `${String(audioRenderState.sectionIndex).padStart(2, '0')}-${slugifyAudioSection(headingText)}`;
                            audioRenderState.sectionOffsets[audioRenderState.currentSectionId] = 0;
                            audioRenderState.sentenceIndexes[audioRenderState.currentSectionId] = 0;
                        }
                        const idAttr = block.id ? ` id="${block.id}"` : '';
                        const childHtml = level === 6
                            ? block.children.map(child => this.renderChild(child)).join('')
                            : this.renderAudioChildren(block.children, audioRenderState);
                        return `<h${level}${idAttr}>${childHtml}</h${level}>`;
                    }
                    case 'list':
                        return `<${block.format === 'ordered' ? 'ol' : 'ul'}>${block.children.map(item => `<li>${this.renderAudioChildren(item.children, audioRenderState)}</li>`).join('')
                            }</${block.format === 'ordered' ? 'ol' : 'ul'}>`;
                    case 'quote':
                        return `<blockquote>${this.renderAudioChildren(block.children, audioRenderState)}</blockquote>`;
                    case 'image':
                        return `<img src="${block.image.url}" alt="${block.image.alternativeText || ''}" width="${block.image.width}" height="${block.image.height}"/>`;
                    default:
                        return block.children ? this.renderAudioChildren(block.children, audioRenderState) : '';
                }
            }).join('');
        },
        getPlainText(children) {
            if (!Array.isArray(children)) return '';

            return children.map(child => {
                if (child.type === 'link') {
                    return this.getPlainText(child.children);
                }

                if (child.children) {
                    return this.getPlainText(child.children);
                }

                return child.text || '';
            }).join('');
        },
        renderAudioChildren(children, audioRenderState) {
            if (!Array.isArray(children)) return '';

            const pieces = this.flattenAudioChildren(children);
            const fullText = pieces.map(piece => piece.text).join('');
            if (!normalizeAudioWhitespace(fullText)) {
                return pieces.map(piece => piece.render(piece.text)).join('');
            }

            const sectionId = audioRenderState.currentSectionId;
            const matches = [...fullText.matchAll(sentencePattern)];
            const ranges = matches.length
                ? matches.map(match => ({ start: match.index, end: match.index + match[0].length, text: match[0] }))
                : [{ start: 0, end: fullText.length, text: fullText }];

            return ranges.map(range => {
                if (!normalizeAudioWhitespace(range.text)) {
                    return this.renderAudioPieceRange(pieces, range.start, range.end);
                }

                const normalizedSentence = normalizeAudioWhitespace(range.text);
                const startChar = audioRenderState.sectionOffsets[sectionId] || 0;
                const endChar = startChar + normalizedSentence.length;
                const sentenceIndex = audioRenderState.sentenceIndexes[sectionId] || 0;
                const sentenceId = `${sectionId}-sentence-${String(sentenceIndex + 1).padStart(3, '0')}`;
                audioRenderState.sectionOffsets[sectionId] = endChar + 1;
                audioRenderState.sentenceIndexes[sectionId] = sentenceIndex + 1;
                const html = this.renderAudioPieceRange(pieces, range.start, range.end);

                return `<span class="story-audio-sentence" id="${sentenceId}" data-audio-section="${sectionId}" data-audio-start="${startChar}" data-audio-end="${endChar}">${html}</span>`;
            }).join('');
        },
        flattenAudioChildren(children) {
            if (!Array.isArray(children)) return [];

            return children.flatMap(child => {
                if (child.type === 'text') {
                    return [{
                        text: child.text || '',
                        render: (text) => {
                            let html = escapeHtml(text).replace(/(?:\r\n|\r|\n)/g, '<br>');
                            if (child.italic) {
                                html = `<em>${html}</em>`;
                            }
                            if (child.bold) {
                                html = `<strong>${html}</strong>`;
                            }
                            return html;
                        },
                    }];
                }

                if (child.type === 'link') {
                    return [{
                        text: this.getPlainText(child.children),
                        render: (text) => `<a href="${escapeHtml(child.url)}" target="_blank">${escapeHtml(text)}</a>`,
                    }];
                }

                if (child.children) {
                    return this.flattenAudioChildren(child.children);
                }

                return [{
                    text: child.text || '',
                    render: (text) => escapeHtml(text),
                }];
            });
        },
        renderAudioPieceRange(pieces, start, end) {
            let cursor = 0;
            let html = '';

            for (const piece of pieces) {
                const pieceStart = cursor;
                const pieceEnd = cursor + piece.text.length;
                cursor = pieceEnd;

                if (pieceEnd <= start || pieceStart >= end) {
                    continue;
                }

                const sliceStart = Math.max(start - pieceStart, 0);
                const sliceEnd = Math.min(end - pieceStart, piece.text.length);
                html += piece.render(piece.text.slice(sliceStart, sliceEnd));
            }

            return html;
        },
        renderChild(child) {
            if (child.type === 'text') {
                // bewaar eerst de ruwe tekst, inclusief enter → <br>
                let text = escapeHtml(child.text).replace(/(?:\r\n|\r|\n)/g, '<br>');

                // Strapi v4 kan ook direct flags geven:
                if (child.italic) {
                    text = `<em>${text}</em>`;
                }
                if (child.bold) {
                    text = `<strong>${text}</strong>`;
                }
                // voeg hier eventueel nog checks toe voor child.underline, child.strikethrough, etc.

                return text;
            }

            if (child.type === 'link') {
                const inner = child.children.map(c => c.text).join('');
                return `<a href="${child.url}" target="_blank">${inner}</a>`;
            }

            // fallback
            return child.text || '';
        },
        formatDate(dateStr) {
            if (!dateStr) return this.$t('blogPost.unknownDate');
            const date = new Date(dateStr);
            if (isNaN(date)) return this.$t('blogPost.unknownDate');
            const localeMap = { nl: 'nl-NL', en: 'en-GB', id: 'id-ID' };
            const dateLocale = localeMap[this.$i18n.locale] || 'nl-NL';
            return date.toLocaleDateString(dateLocale, {
                day: '2-digit',
                month: 'long',
                year: 'numeric'
            });
        },
        setDocumentTitle() {
            if (this.post && this.post.title) {
                document.title = `${this.post.title} | Sejarah Thümann`;
            } else {
                document.title = 'Sejarah Thümann';
            }
        },
        scrollToSection(sectionId) {
            const section = document.getElementById(sectionId);
            if (section) {
                const offset = 72; // Adjust if you have a fixed header
                const position = section.getBoundingClientRect().top + window.scrollY - offset;
                window.scrollTo({
                    top: position,
                    behavior: 'smooth'
                });
            }
        },
        updateAnchorHeight() {
            if (this.$refs.anchorLinks) {
                this.anchorHeight = this.$refs.anchorLinks.offsetHeight;
            }
        },
        forceReload(url) {
            window.location.href = url;
        },
        selectAudioChunk(index, shouldPlay = this.isStoryAudioPlaying) {
            if (index < 0 || index >= this.storyAudioChunks.length) {
                return;
            }

            this.clearStoryAudioSectionPause();
            this.audioCurrentIndex = index;
            this.currentAudioTime = 0;
            this.currentAudioDuration = 0;
            this.$nextTick(() => {
                this.updateAudioProgress();
                if (shouldPlay) {
                    this.playCurrentAudio();
                }
            });
        },
        toggleStoryAudio() {
            if (!this.$refs.storyAudioPlayer) {
                return;
            }

            if (this.isStoryAudioPlaying) {
                this.clearStoryAudioSectionPause();
                this.$refs.storyAudioPlayer.pause();
                this.isStoryAudioPlaying = false;
                return;
            }

            this.playCurrentAudio();
        },
        playCurrentAudio() {
            if (!this.$refs.storyAudioPlayer) {
                return;
            }

            this.$refs.storyAudioPlayer.play().catch(() => {
                this.isStoryAudioPlaying = false;
            });
        },
        playNextAudioChunk() {
            if (this.audioCurrentIndex < this.storyAudioChunks.length - 1) {
                const currentChunk = this.storyAudioChunks[this.audioCurrentIndex];
                const nextIndex = this.audioCurrentIndex + 1;
                const nextChunk = this.storyAudioChunks[nextIndex];
                const shouldPauseForSection = currentChunk?.sectionId && nextChunk?.sectionId && currentChunk.sectionId !== nextChunk.sectionId;
                this.isStoryAudioPlaying = true;
                if (shouldPauseForSection) {
                    this.storyAudioSectionPauseTimer = window.setTimeout(() => {
                        this.storyAudioSectionPauseTimer = null;
                        this.selectAudioChunk(nextIndex, true);
                    }, SECTION_PAUSE_MS);
                } else {
                    this.selectAudioChunk(nextIndex, true);
                }
            } else {
                this.isStoryAudioPlaying = false;
                this.currentAudioTime = 0;
                this.clearActiveAudioSentence();
            }
        },
        handleAudioPause() {
            if (!this.storyAudioSectionPauseTimer) {
                this.isStoryAudioPlaying = false;
            }
        },
        clearStoryAudioSectionPause() {
            if (this.storyAudioSectionPauseTimer) {
                window.clearTimeout(this.storyAudioSectionPauseTimer);
                this.storyAudioSectionPauseTimer = null;
            }
        },
        updateAudioProgress() {
            const audio = this.$refs.storyAudioPlayer;
            if (!audio) {
                return;
            }

            this.applyPendingAudioSeek(audio);
            this.currentAudioTime = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;
            this.currentAudioDuration = Number.isFinite(audio.duration) ? audio.duration : 0;
            if (this.currentAudioDuration && !this.storyAudioDurations[this.audioCurrentIndex]) {
                this.storyAudioDurations.splice(this.audioCurrentIndex, 1, this.currentAudioDuration);
            }
        },
        applyPendingAudioSeek(audio) {
            if (this.pendingAudioSeekTime === null || !Number.isFinite(audio.duration)) {
                return;
            }

            audio.currentTime = Math.min(Math.max(this.pendingAudioSeekTime, 0), audio.duration);
            this.pendingAudioSeekTime = null;
        },
        handleAudioTimeUpdate() {
            this.updateAudioProgress();
            this.updateActiveAudioSentence();
        },
        updateActiveAudioSentence() {
            const chunk = this.storyAudioChunks[this.audioCurrentIndex];
            const duration = this.currentAudioDuration;
            if (!chunk || !duration) {
                return;
            }

            const startChar = Number.isFinite(chunk.startChar) ? chunk.startChar : 0;
            const endChar = Number.isFinite(chunk.endChar) ? chunk.endChar : startChar + (chunk.characters || 1);
            const progress = Math.min(Math.max(this.currentAudioTime / duration, 0), 1);
            const currentChar = startChar + ((endChar - startChar) * progress);
            const selector = `.story-audio-sentence[data-audio-section="${chunk.sectionId}"]`;
            const sentences = Array.from(document.querySelectorAll(selector));
            const activeSentence = sentences.find(sentence => {
                const sentenceStart = Number(sentence.dataset.audioStart);
                const sentenceEnd = Number(sentence.dataset.audioEnd);
                return currentChar >= sentenceStart && currentChar <= sentenceEnd;
            }) || sentences.find(sentence => Number(sentence.dataset.audioStart) >= currentChar) || sentences[sentences.length - 1];

            if (activeSentence) {
                this.setActiveAudioSentence(activeSentence);
            }
        },
        setActiveAudioSentence(sentenceElement) {
            if (this.activeAudioSentenceId === sentenceElement.id) {
                return;
            }

            this.clearActiveAudioSentence();
            sentenceElement.classList.add('story-audio-sentence--active');
            this.activeAudioSentenceId = sentenceElement.id;
            sentenceElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        },
        clearActiveAudioSentence() {
            if (this.activeAudioSentenceId) {
                const previous = document.getElementById(this.activeAudioSentenceId);
                if (previous) {
                    previous.classList.remove('story-audio-sentence--active');
                }
            }
            this.activeAudioSentenceId = null;
        },
        seekStoryAudio(event) {
            if (!this.storyAudioTotalDuration) {
                return;
            }

            this.clearStoryAudioSectionPause();
            const rect = event.currentTarget.getBoundingClientRect();
            const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
            this.seekStoryAudioTo(this.storyAudioTotalDuration * ratio);
        },
        seekStoryAudioBy(seconds) {
            if (!this.storyAudioTotalDuration) {
                return;
            }

            this.clearStoryAudioSectionPause();
            const targetTime = Math.min(Math.max(this.storyAudioElapsedTime + seconds, 0), this.storyAudioTotalDuration);
            this.seekStoryAudioTo(targetTime);
        },
        seekStoryAudioTo(totalSeconds, forcePlay = false) {
            const target = this.findAudioChunkAtTime(totalSeconds);
            if (!target) {
                return;
            }

            const shouldPlay = forcePlay || this.isStoryAudioPlaying;
            this.audioCurrentIndex = target.index;
            this.pendingAudioSeekTime = target.offset;
            this.$nextTick(() => {
                const audio = this.$refs.storyAudioPlayer;
                if (!audio) {
                    return;
                }

                this.applyPendingAudioSeek(audio);
                this.currentAudioTime = audio.currentTime || target.offset;
                this.updateActiveAudioSentence();
                if (shouldPlay) {
                    this.playCurrentAudio();
                }
            });
        },
        findAudioChunkAtTime(totalSeconds) {
            let elapsed = 0;

            for (let index = 0; index < this.storyAudioChunks.length; index += 1) {
                const duration = this.storyAudioDurations[index] || (index === this.audioCurrentIndex ? this.currentAudioDuration : 0);
                const isLastChunk = index === this.storyAudioChunks.length - 1;
                if (!duration && !isLastChunk) {
                    continue;
                }

                if (totalSeconds <= elapsed + duration || isLastChunk) {
                    return {
                        index,
                        offset: Math.max(Math.min(totalSeconds - elapsed, duration || 0), 0),
                    };
                }

                elapsed += duration;
            }

            return null;
        },
        playStoryAudioFromStart() {
            if (!this.storyAudioTotalDuration) {
                return;
            }

            this.clearStoryAudioSectionPause();
            this.seekStoryAudioTo(0, true);
        },
        playStoryAudioFromText(event) {
            if (!this.storyAudioTotalDuration || event.target.closest?.('a')) {
                return;
            }

            const target = event.target.closest?.('.story-audio-sentence');
            if (!target) {
                return;
            }

            const totalSeconds = this.findAudioTimeForTextTarget(target);
            if (totalSeconds === null) {
                return;
            }

            this.clearStoryAudioSectionPause();
            this.setActiveAudioSentence(target);
            this.seekStoryAudioTo(totalSeconds, true);
        },
        findAudioTimeForTextTarget(target) {
            const sectionId = target.dataset.audioSection;
            const startChar = Number(target.dataset.audioStart);
            if (!sectionId || !Number.isFinite(startChar)) {
                return null;
            }

            let elapsed = 0;
            for (let index = 0; index < this.storyAudioChunks.length; index += 1) {
                const chunk = this.storyAudioChunks[index];
                const duration = this.storyAudioDurations[index] || (index === this.audioCurrentIndex ? this.currentAudioDuration : 0);
                const chunkStart = Number.isFinite(chunk.startChar) ? chunk.startChar : 0;
                const chunkEnd = Number.isFinite(chunk.endChar) ? chunk.endChar : chunkStart + (chunk.characters || 0);

                if (chunk.sectionId === sectionId && startChar >= chunkStart && startChar <= chunkEnd) {
                    const chunkProgress = chunkEnd > chunkStart ? (startChar - chunkStart) / (chunkEnd - chunkStart) : 0;
                    return elapsed + (duration * Math.min(Math.max(chunkProgress, 0), 1));
                }

                elapsed += duration;
            }

            return null;
        }
    },
    computed: {
        storyAudioChunks() {
            if (!this.storyAudio || !Array.isArray(this.storyAudio.sections)) {
                return [];
            }

            return this.storyAudio.sections.flatMap(section =>
                (section.chunks || []).map(chunk => ({
                    ...chunk,
                    sectionId: section.id,
                }))
            );
        },
        currentAudioSrc() {
            return this.storyAudioChunks[this.audioCurrentIndex]?.file || null;
        },
        storyAudioTotalDuration() {
            return this.storyAudioDurations.reduce((total, duration) => total + (duration || 0), 0);
        },
        storyAudioElapsedTime() {
            const previousDuration = this.storyAudioDurations
                .slice(0, this.audioCurrentIndex)
                .reduce((total, duration) => total + (duration || 0), 0);

            return previousDuration + this.currentAudioTime;
        },
        storyAudioProgress() {
            if (!this.storyAudioTotalDuration) {
                return 0;
            }

            return Math.min(Math.max((this.storyAudioElapsedTime / this.storyAudioTotalDuration) * 100, 0), 100);
        },
        formattedStoryAudioElapsed() {
            return formatAudioTime(this.storyAudioElapsedTime);
        },
        formattedStoryAudioTotal() {
            return formatAudioTime(this.storyAudioTotalDuration);
        },
    }
};
</script>

<style>
.anchor-links {
    position: sticky;
    top: 24px;
    overflow: auto;
    border-radius: 16px;
    background-color: #fff;
    width: 360px;
    left: 24px;
    z-index: 10;
    box-shadow: var(--shadow-card-lg);
    display: none;
}

.anchor-links h1 {
    font-family: var(--font-primary);
    color: var(--brand-color-1);
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 125%;
    text-transform: none;
    padding: 16px 24px;
    border-bottom: 1px solid #f0f0f0;
}

.anchor-links ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
}

.anchor-links li a {
    text-decoration: none;
    cursor: pointer;
    color: var(--brand-text-dark);
    padding: 8px calc(24px + 28px + 8px) 8px 24px;
    font-weight: 600;
    border-bottom: 1px solid #f0f0f0;
}

.anchor-links li a:hover {
    text-decoration: underline;
    background-color: #f0f0f0;
}

.back_button {
    cursor: default;
    font-size: 12px;
    text-transform: uppercase;
}

.back_button span {
    cursor: pointer;
    font-weight: 600;
}

.back_button span:hover {
    text-decoration: underline;
}

.suggested_blog_post {
    width: calc(33.333% - 13.333px);
}

.loader {
    padding: 120px 20px 144px 20px;
}

.table-of-contents {
    width: 100%;
    max-width: 708px;
    margin-bottom: 8px;
}

.table-of-contents h2 {
    margin: 32px 0;
}

.table-of-contents ul {
    /* list-style: none;
    padding: 0;
    margin: 0; */
    border-top: 1px solid rgba(29, 29, 29, 0.2);
}

.table-of-contents a {
    /* color: var(--brand-color-1);
    text-decoration: none;
    cursor: pointer; */
    color: var(--brand-text-dark) !important;
    width: 100%;
    text-decoration: none !important;
    position: relative;
    padding: 8px 0;
    cursor: pointer;
    font-weight: 600;
    border-bottom: 1px solid rgba(29, 29, 29, 0.2);
}

.table-of-contents a:hover {
    text-decoration: underline !important;
}

.story-audio {
    width: fit-content;
    min-width: 360px;
    padding: 18px 20px;
    border: 1px solid rgba(29, 29, 29, 0.14);
    background: #fff;
    position: fixed;
    bottom: 24px;
    left: 24px;
    z-index: 10;
    /* transform: translateX(-50%); */
    border-radius: 4px;
}

.story-audio__label {
    margin: 0 0 10px;
    color: var(--brand-text-dark);
    font-size: 13px;
    font-weight: 700;
    line-height: 1.2;
    opacity: 0.64;
}

.story-audio audio {
    display: none;
}

.story-audio__main {
    display: flex;
    align-items: center;
    gap: 16px;
}

.story-audio__controls {
    display: flex;
    align-items: center;
    justify-content: center;
    /* flex: 0 0 48px; */
    /* margin-left: -64px; */
}

.story-audio__timeline {
    flex: 1 1 auto;
    min-width: 0;
    width: 100%;
}

.story-audio__play {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    padding: 0;
    border: 1px solid var(--brand-color-1);
    border-radius: 50%;
    cursor: pointer;
    background: var(--brand-color-1);
    color: #fff;
}

.story-audio__play:disabled {
    cursor: not-allowed;
    opacity: 0.45;
}

.story-audio__play-icon {
    display: block;
    width: 0;
    height: 0;
    margin-left: 3px;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-left: 16px solid currentColor;
}

.story-audio__play-icon--pause {
    width: 16px;
    height: 20px;
    margin-left: 0;
    border: 0;
    background: linear-gradient(90deg, currentColor 0 35%, transparent 35% 65%, currentColor 65% 100%);
}

.story-audio__progress {
    width: 100%;
    height: 8px;
    overflow: hidden;
    background: rgba(29, 29, 29, 0.12);
    cursor: pointer;
    border-radius: 999px;
}

.story-audio__progress:focus-visible {
    outline: 2px solid var(--brand-color-1);
    outline-offset: 4px;
}

.story-audio__progress span {
    display: block;
    height: 100%;
    background: var(--brand-color-1);
    transition: width 160ms linear;
}

.story-audio__time {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-top: 8px;
    color: var(--brand-text-dark);
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    opacity: 0.72;
}

.story-audio-title-target,
.story-audio-sentence {
    position: relative;
    border-radius: 2px;
    transition: background-color 180ms ease;
}

.story-audio-title-target {
    cursor: pointer;
}

.post_content--audio-selectable .story-audio-sentence {
    cursor: pointer;
}

.story-audio-title-target:hover,
.post_content--audio-selectable .story-audio-sentence:hover,
.story-audio-sentence--active {
    background-color: rgba(255, 204, 51, 0.18);
}

.story-audio-title-target:hover::after,
.post_content--audio-selectable .story-audio-sentence:hover::after,
.story-audio-sentence--active::after {
    content: '';
    position: absolute;
    left: -0.08em;
    right: -0.08em;
    bottom: -0.2em;
    height: 0.45em;
    pointer-events: none;
    border-bottom: 4px solid rgba(255, 204, 51, 0.95);
    border-radius: 50%;
}

@media (max-width: 900px) {
    .story-audio {
        right: 16px;
        left: 16px;
        width: auto;
        min-width: 0;
        padding: 16px;
    }

    .story-audio__controls {
        margin-left: 0;
    }
}

em {
    background-color: rgba(255, 204, 51, .25);
    font-style: italic !important;
}

.post_video {
    position: relative;
    width: 100%;
    padding-bottom: 56.25%;
    /* 16:9 aspect ratio */
    height: 0;
    /* margin-bottom: 32px; */
}

.post_video iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}
</style>
